import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { useAppEnvironment } from './AppEnvironmentContext';

function routeId(location: ReturnType<typeof useLocation>) {
  return `${location.pathname}${location.search}${location.hash}`;
}

export function useTelegramBackButton() {
  const { isTelegram, telegram } = useAppEnvironment();
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const routeStackRef = useRef<string[]>([]);
  const currentRoute = routeId(location);
  const isRootRoute = location.pathname === '/';

  useEffect(() => {
    const stack = routeStackRef.current;
    const lastRoute = stack[stack.length - 1];
    if (lastRoute === currentRoute) return;

    if (stack.length === 0) {
      stack.push(currentRoute);
      return;
    }

    if (navigationType === 'REPLACE') {
      stack[stack.length - 1] = currentRoute;
      return;
    }

    if (navigationType === 'POP') {
      const previousIndex = stack.lastIndexOf(currentRoute);
      if (previousIndex >= 0) {
        stack.splice(previousIndex + 1);
      } else {
        stack.push(currentRoute);
      }
      return;
    }

    stack.push(currentRoute);
  }, [currentRoute, navigationType]);

  const handleBack = useCallback(() => {
    const hasInternalHistory = routeStackRef.current.length > 1;
    if (hasInternalHistory) {
      navigate(-1);
      return;
    }

    if (!isRootRoute) {
      navigate('/', { replace: true });
    }
  }, [isRootRoute, navigate]);

  useEffect(() => {
    const backButton = telegram.webApp?.BackButton;
    if (!isTelegram || !backButton) return;

    if (isRootRoute) {
      backButton.hide();
    } else {
      backButton.show();
    }

    backButton.onClick(handleBack);

    return () => {
      backButton.offClick(handleBack);
      backButton.hide();
    };
  }, [handleBack, isRootRoute, isTelegram, telegram.webApp]);
}
